/**
 * ToolNode - Tool invocation node
 * Represents a configurable tool in the workflow
 */

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { ToolNodeData } from '../../../../stores/workflow/types';

interface ToolNodeProps {
  data: ToolNodeData;
  selected?: boolean;
}

export const ToolNode = memo(({ data, selected }: ToolNodeProps) => {
  return (
    <div
      className={`
        min-w-[160px] rounded-xl border-2 bg-white shadow-lg transition-all
        dark:bg-dark-background-secondary
        ${selected ? 'border-indigo-400 ring-4 ring-indigo-200/50' : 'border-gray-200 dark:border-border-dark'}
        ${!data.isConfigured ? 'border-dashed border-yellow-400' : ''}
      `}
    >
      {/* Input Handle (top) */}
      <Handle type='target' position={Position.Top} className='!h-3 !w-3 !border-2 !border-white !bg-gray-400' />

      {/* Header */}
      <div className='flex items-center gap-2 rounded-t-lg bg-gradient-to-r from-indigo-500 to-indigo-700 px-4 py-2'>
        {/* Tool Icon */}
        <div className='text-white'>
          <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
            />
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
          </svg>
        </div>

        <div className='flex-1 font-semibold text-white'>{data.label}</div>

        {!data.isConfigured && <div className='rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-medium text-yellow-900'>Configure</div>}
      </div>

      {/* Body */}
      <div className='px-4 py-3'>
        {/* Tool Type */}
        <div className='mb-2 flex items-center gap-2'>
          <span className='text-xs text-gray-500 dark:text-dark-text-secondary'>Type:</span>
          <span className='rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'>
            {data.toolType || 'Unknown'}
          </span>
        </div>

        {/* Tool ID */}
        {data.toolId && <div className='truncate text-xs text-gray-400 dark:text-dark-text-secondary'>ID: {data.toolId}</div>}

        {/* Config Preview */}
        {data.config && Object.keys(data.config).length > 0 && (
          <div className='mt-2 flex items-center gap-1 text-xs text-gray-400'>
            <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'
              />
            </svg>
            <span>{Object.keys(data.config).length} params configured</span>
          </div>
        )}
      </div>

      {/* Output Handle (bottom) */}
      <Handle type='source' position={Position.Bottom} className='!h-3 !w-3 !border-2 !border-white !bg-indigo-500' />
    </div>
  );
});

ToolNode.displayName = 'ToolNode';
